/**
 * PORTED (REWRITTEN) — RO-3-00d / #44471.
 * Origin: `excellent/app/projects/[projectSlug]/[sectionSlug]/_crm/settings.tsx`.
 *
 * Each settings sub-view composes already-mounted CRM list ops (pipelines,
 * statuses, sources, templates, sequences, email/sms, forecast target). Editors
 * come from the co-ported settings-client. Integrations panel renders empty
 * providers until a product-provider list contract is mounted (hub still links).
 */

import { ProductIntegrationsPanel } from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { crm } from "@excellent/ui/api/contracts/crm";
import { DataImportClient } from "../data-import/client";
import {
  CrmEmailTransportEditorClient,
  CrmForecastTargetEditorClient,
  CrmPipelinesEditorClient,
  CrmSequencesEditorClient,
  CrmSmartViewsEditorClient,
  CrmSmsTransportEditorClient,
  CrmSourcesEditorClient,
  CrmStatusesEditorClient,
  CrmTemplatesEditorClient,
  SeedApplyClient,
} from "./settings-client";

export function CrmPipelinesSettings() {
  const { data: pipelinesData } = useApi(crm.pipelines.list, {});
  const { data: targetData } = useApi(crm.settings.forecast_target_get, {});
  const pipelines = pipelinesData?.pipelines ?? [];

  return (
    <>
      <section id="pipelines" className="scroll-mt-24">
        <CrmPipelinesEditorClient
          pipelines={pipelines as Parameters<typeof CrmPipelinesEditorClient>[0]["pipelines"]}
        />
      </section>
      <section id="forecast-target" className="scroll-mt-24">
        <CrmForecastTargetEditorClient initialMinor={targetData?.minor ?? null} />
      </section>
    </>
  );
}

export function CrmLeadsSettings() {
  const { data: statusesData } = useApi(crm.settings.statuses_list, {});
  const { data: sourcesData } = useApi(crm.settings.sources_list, {});
  const { data: smartData } = useApi(crm.smart_views.list, { query: {} });
  const statuses = statusesData?.statuses ?? [];
  const sources = sourcesData?.sources ?? [];
  const smartViews = (smartData?.smart_views ?? []).map((v) => ({
    id: v.id,
    slug: v.slug,
    name: v.name,
    entity_kind: v.entity_kind,
    shared: v.shared,
    query_json: v.query_json,
  }));

  return (
    <>
      <section id="lead-statuses" className="scroll-mt-24">
        <CrmStatusesEditorClient statuses={statuses} />
      </section>
      <section id="lead-sources" className="scroll-mt-24">
        <CrmSourcesEditorClient sources={sources} />
      </section>
      <section id="smart-views" className="scroll-mt-24">
        <CrmSmartViewsEditorClient views={smartViews} />
      </section>
    </>
  );
}

export function CrmMessagingSettings() {
  const { data: templatesData } = useApi(crm.templates.list, {});
  const { data: sequencesData } = useApi(crm.sequences.list, {});
  const templates = templatesData?.templates ?? [];
  const sequences = sequencesData?.sequences ?? [];

  return (
    <>
      <section id="email-templates" className="scroll-mt-24">
        <CrmTemplatesEditorClient
          title="Email templates"
          templates={templates.map((t) => ({
            id: t.id,
            name: t.name,
            subject: t.subject,
            body: t.body_text,
            slug: t.slug,
          }))}
        />
      </section>
      <section id="sequences" className="scroll-mt-24">
        <CrmSequencesEditorClient
          sequences={sequences.map((s) => ({
            id: s.id,
            slug: s.slug,
            name: s.name,
            description: s.description,
            paused: s.paused,
            // List op has no embedded steps; editor loads empty until expand.
            steps: [] as Array<{
              id: number;
              sort_key: string;
              day_offset: number;
              channel: "email" | "sms" | "call" | "task";
              template_id: number | null;
              instructions: string | null;
            }>,
          }))}
          templates={templates.map((t) => ({ id: t.id, name: t.name }))}
        />
      </section>
    </>
  );
}

export function CrmSendingSettings() {
  const { data: emailData } = useApi(crm.settings.email_get, {});
  const { data: smsData } = useApi(crm.settings.sms_get, {});

  return (
    <>
      <section id="email-sending" className="scroll-mt-24">
        <CrmEmailTransportEditorClient
          initial={(emailData ?? {}) as Parameters<
            typeof CrmEmailTransportEditorClient
          >[0]["initial"]}
          keyFromEnv={Boolean(emailData && (emailData as { keyFromEnv?: boolean }).keyFromEnv)}
        />
      </section>
      <section id="sms-sending" className="scroll-mt-24">
        <CrmSmsTransportEditorClient
          initial={(smsData ?? {}) as Parameters<
            typeof CrmSmsTransportEditorClient
          >[0]["initial"]}
          keyFromEnv={Boolean(smsData && (smsData as { keyFromEnv?: boolean }).keyFromEnv)}
        />
      </section>
    </>
  );
}

export function CrmDataSettings() {
  return (
    <>
      <section id="import" className="scroll-mt-24">
        <DataImportClient targetKind="crm-leads" />
      </section>
      <section id="seed-data" className="scroll-mt-24">
        <SeedApplyClient />
      </section>
      <section id="integrations" className="scroll-mt-24">
        <ProductIntegrationsPanel productLabel="CRM" providers={[]} />
      </section>
    </>
  );
}

export function CrmSettingsView() {
  return (
    <>
      <CrmPipelinesSettings />
      <CrmLeadsSettings />
      <CrmMessagingSettings />
      <CrmSendingSettings />
      <CrmDataSettings />
    </>
  );
}
